import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

import { Box, Button, CircularProgress, Typography } from '@mui/material';
import CicCommentTemplatePicker from 'app/shared/components/CicCommentTemplatePicker';
import CicValueChange from 'app/shared/components/CicValueChange';
import FieldHelper from 'app/shared/components/FieldHelper';
import { AutocompleteCDS, TextFieldCDS } from 'app/shared/componentsRestyled/coreDesignSystem/form';
import CicReasonOption from 'app/shared/modules/CicSaveReasonModal/components/CicFieldReasonsForm/components/CicReasonOption';
import PropTypes from 'prop-types';

import { CIC_SAVE_NOTE_MAX_LENGTH } from 'constants/cicReason';
import { HTTP_RESPONSE_STATUS_CODES } from 'constants/http';
import useLoanId from 'hooks/useLoanId';
import { getCicDisplayValue, getReasonsForFee } from 'utils/cicReason';

import {
  editLoanCicChangeAction,
  editLoanCicFeeChangeAction,
} from 'store/loanApplications/actions';
import {
  getActiveCicFeeReasonsAction,
  getActiveCicLoanTermReasonsAction,
} from 'store/system/actions';

import { EDIT_HISTORY_TITLE } from '../../LoanCicHistory.constants';
import { cicChangePropTypes, cicFeeChangePropTypes } from '../../LoanCicHistory.propTypes';
import { LoanCicHistoryTestIds } from '../../LoanCicHistory.testIds';
import CicEditHistoryList from '../CicEditHistoryList';

import {
  CANCEL_BUTTON_LABEL,
  CHANGE_LABEL,
  CLOSE_BUTTON_LABEL,
  COMMENT_HELPER_TEXT,
  COMMENT_LABEL,
  CONFLICT_MESSAGE,
  ENTERED_BY_LABEL,
  FEE_CONFLICT_MESSAGE,
  FEE_LABEL,
  INACTIVE_REASON_MESSAGE,
  LOAD_REASONS_ERROR_MESSAGE,
  LOG_NOTE,
  REASON_LABEL,
  REASON_PLACEHOLDER,
  SAVE_BUTTON_LABEL,
} from './CicEditReasonForm.constants';
import {
  footerButtonsStyles,
  footerStyles,
  formStyles,
  historyTitleStyles,
  loaderStyles,
  sectionStyles,
  summaryStyles,
} from './CicEditReasonForm.styles';
import {
  getDialogSubtitle,
  getEditableUntilMessage,
  getEnteredByLabel,
  getIsCommentValid,
  getIsFormChanged,
  getSaveErrorMessage,
} from './CicEditReasonForm.utils';
import CicEditReasonNotice from './components/CicEditReasonNotice';

const CicEditReasonForm = ({
  change,
  bundleType = null,
  plannedTriggerDate = null,
  isFeeChange = false,
  onCancel,
  onSaved,
  onConflict,
}) => {
  const dispatch = useDispatch();
  const loanId = useLoanId();

  const [reasons, setReasons] = useState([]);
  const [isLoadingReasons, setIsLoadingReasons] = useState(true);
  const [isReasonsLoadFailed, setIsReasonsLoadFailed] = useState(false);
  const [cicReasonId, setCicReasonId] = useState(change.cicReasonId);
  const [comment, setComment] = useState(change.comment ?? '');
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [isConflict, setIsConflict] = useState(false);

  const selectedReason = useMemo(
    () => reasons.find(({ cicFeeReasonId }) => cicFeeReasonId === cicReasonId) ?? null,
    [reasons, cicReasonId],
  );

  const isCurrentReasonInactive =
    !isLoadingReasons &&
    !isReasonsLoadFailed &&
    cicReasonId === change.cicReasonId &&
    !selectedReason;
  const isCommentValid = getIsCommentValid(comment);
  const isChanged = getIsFormChanged({ change, cicReasonId, comment });
  const isDisabled = isSaving || isConflict;
  const isSaveDisabled =
    isDisabled || isLoadingReasons || !selectedReason || !isCommentValid || !isChanged;
  const editableUntilMessage = getEditableUntilMessage({ bundleType, plannedTriggerDate });
  const conflictMessage = isFeeChange ? FEE_CONFLICT_MESSAGE : CONFLICT_MESSAGE;

  const onReasonChange = useCallback((e, option) => {
    setCicReasonId(option?.cicFeeReasonId ?? null);
    setSaveError('');
  }, []);

  const onCommentChange = useCallback(e => {
    setComment(e.target.value);
    setSaveError('');
  }, []);

  const onTemplateApply = useCallback(text => {
    setComment(text);
    setSaveError('');
  }, []);

  const onSave = useCallback(async () => {
    setIsSaving(true);
    setSaveError('');

    try {
      const editParams = { loanId, cicReasonId, comment: comment.trim(), silent: true };
      const updatedChange = await dispatch(
        isFeeChange
          ? editLoanCicFeeChangeAction({ ...editParams, feeChangeId: change.id })
          : editLoanCicChangeAction({ ...editParams, changeId: change.id }),
      );

      onSaved(updatedChange);
    } catch (error) {
      const isConflictError = error?.response?.status === HTTP_RESPONSE_STATUS_CODES.CONFLICT;

      setIsConflict(isConflictError);
      setSaveError(isConflictError ? '' : getSaveErrorMessage(error));

      if (isConflictError) {
        onConflict();
      }
    } finally {
      setIsSaving(false);
    }
  }, [dispatch, loanId, change.id, cicReasonId, comment, isFeeChange, onSaved, onConflict]);

  useEffect(() => {
    const loadReasonsAction = isFeeChange
      ? getActiveCicFeeReasonsAction
      : getActiveCicLoanTermReasonsAction;

    dispatch(loadReasonsAction({ silent: true }))
      .then(activeReasons =>
        setReasons(isFeeChange ? getReasonsForFee(activeReasons, change.feeTypeId) : activeReasons),
      )
      .catch(() => setIsReasonsLoadFailed(true))
      .finally(() => setIsLoadingReasons(false));
  }, [dispatch, isFeeChange, change.feeTypeId]);

  return (
    <Box data-testid={LoanCicHistoryTestIds.EDIT_REASON_FORM} sx={formStyles}>
      <Typography color="text.secondary" variant="regular12">
        {getDialogSubtitle({ label: isFeeChange ? change.feeName : change.label, bundleType })}
      </Typography>

      {isConflict && (
        <CicEditReasonNotice
          isError
          dataTestId={LoanCicHistoryTestIds.EDIT_ERROR_LABEL}
          message={conflictMessage}
        />
      )}

      {!isConflict && !!editableUntilMessage && (
        <CicEditReasonNotice message={editableUntilMessage} />
      )}

      <Box sx={summaryStyles}>
        {isFeeChange && (
          <>
            <Typography color="text.secondary" variant="regular14">
              {FEE_LABEL}
            </Typography>
            <Typography data-testid={LoanCicHistoryTestIds.EDIT_FEE_LABEL} variant="medium14">
              {getCicDisplayValue(change.feeName)}
            </Typography>
          </>
        )}
        <Typography color="text.secondary" variant="regular14">
          {CHANGE_LABEL}
        </Typography>
        <CicValueChange newDisplay={change.newDisplay} oldDisplay={change.oldDisplay} />
        <Typography color="text.secondary" variant="regular14">
          {ENTERED_BY_LABEL}
        </Typography>
        <Typography variant="regular14">{getEnteredByLabel(change)}</Typography>
      </Box>

      {isLoadingReasons ? (
        <Box sx={loaderStyles}>
          <CircularProgress size={24} />
        </Box>
      ) : (
        <Box sx={sectionStyles}>
          <AutocompleteCDS
            disableClearable
            required
            OptionContent={CicReasonOption}
            dataTestId={LoanCicHistoryTestIds.EDIT_REASON_SELECT}
            dataTestIdInput={LoanCicHistoryTestIds.EDIT_REASON_INPUT}
            dataTestIdOption={LoanCicHistoryTestIds.EDIT_REASON_OPTION}
            disabled={isDisabled || isReasonsLoadFailed}
            label={REASON_LABEL}
            name="cicReasonId"
            optionKey="cicFeeReasonId"
            options={reasons}
            placeholder={REASON_PLACEHOLDER}
            value={selectedReason}
            variant="outlined"
            withSortedOptions={false}
            onChange={onReasonChange}
          />
          {(isReasonsLoadFailed || isCurrentReasonInactive || !!selectedReason?.description) && (
            <Typography
              color={isReasonsLoadFailed ? 'error.main' : 'text.secondary'}
              variant="regular12"
            >
              {(isReasonsLoadFailed && LOAD_REASONS_ERROR_MESSAGE) ||
                (isCurrentReasonInactive && INACTIVE_REASON_MESSAGE) ||
                selectedReason.description}
            </Typography>
          )}
        </Box>
      )}

      <CicCommentTemplatePicker
        commentTemplates={selectedReason?.commentTemplates}
        dataTestId={LoanCicHistoryTestIds.EDIT_TEMPLATE_SELECT}
        dataTestIdInput={LoanCicHistoryTestIds.EDIT_TEMPLATE_INPUT}
        dataTestIdOption={LoanCicHistoryTestIds.EDIT_TEMPLATE_OPTION}
        isDisabled={isDisabled}
        name="commentTemplate"
        reasonId={cicReasonId}
        onApply={onTemplateApply}
      />

      <Box>
        <TextFieldCDS
          multiline
          required
          dataTestId={LoanCicHistoryTestIds.EDIT_COMMENT_INPUT}
          disabled={isDisabled}
          error={!isCommentValid}
          label={COMMENT_LABEL}
          maxLength={CIC_SAVE_NOTE_MAX_LENGTH}
          name="comment"
          rows={3}
          value={comment}
          variant="outlined"
          onChange={onCommentChange}
        />
        <FieldHelper
          length={comment.length}
          maxLength={CIC_SAVE_NOTE_MAX_LENGTH}
          text={COMMENT_HELPER_TEXT}
        />
      </Box>

      {!!saveError && (
        <CicEditReasonNotice
          isError
          dataTestId={LoanCicHistoryTestIds.EDIT_ERROR_LABEL}
          message={saveError}
        />
      )}

      {!isFeeChange && (
        <Box sx={sectionStyles}>
          <Typography sx={historyTitleStyles} variant="bold12">
            {EDIT_HISTORY_TITLE}
          </Typography>
          <CicEditHistoryList change={change} />
        </Box>
      )}

      <Box sx={footerStyles}>
        <Typography color="text.secondary" variant="regular12">
          {isFeeChange ? '' : LOG_NOTE}
        </Typography>

        <Box sx={footerButtonsStyles}>
          <Button
            data-testid={LoanCicHistoryTestIds.EDIT_CANCEL_BUTTON}
            size="large"
            variant="line"
            onClick={onCancel}
          >
            {isConflict ? CLOSE_BUTTON_LABEL : CANCEL_BUTTON_LABEL}
          </Button>
          <Button
            data-testid={LoanCicHistoryTestIds.EDIT_SAVE_BUTTON}
            disabled={isSaveDisabled}
            size="large"
            onClick={onSave}
          >
            {SAVE_BUTTON_LABEL}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

CicEditReasonForm.propTypes = {
  change: PropTypes.oneOfType([cicChangePropTypes, cicFeeChangePropTypes]).isRequired,
  onCancel: PropTypes.func.isRequired,
  onConflict: PropTypes.func.isRequired,
  onSaved: PropTypes.func.isRequired,
  bundleType: PropTypes.string,
  isFeeChange: PropTypes.bool,
  plannedTriggerDate: PropTypes.string,
};

export default CicEditReasonForm;
